import { z } from '@customerio/hydra-validation';
import { isEmptyString } from '@/utils/string';
import { isAbsoluteHttpUrl } from '@/utils/url';

const CIO_DOMAIN_REGEX = /https?:\/\/.*?\.?customer\.io/;

export const MAX_NAME_LENGTH = 191;

/** Whether the endpoint points at a Customer.io domain, which the backend rejects. */
export function isCustomerIoDomain(value: string): boolean {
  return CIO_DOMAIN_REGEX.test(value);
}

export { isAbsoluteHttpUrl };

/**
 * Field names are the wire's, so a validated form maps onto the write body with no rename layer.
 *
 * The endpoint checks run as one ladder rather than chained refinements because the order decides
 * which message the user sees: blank, then Customer.io domain, then malformed.
 */
export const reportingWebhookSchema = z.object({
  name: z
    .string()
    .refine((value) => value.trim() !== '', { message: "Name can't be blank" })
    .refine((value) => value.length <= MAX_NAME_LENGTH, {
      message: `Name can't be longer than ${MAX_NAME_LENGTH} characters`,
    }),
  // Trimmed on the way through: the saved endpoint must not carry the whitespace a paste brings.
  endpoint_url: z
    .string()
    .transform((value) => value.trim())
    .superRefine((value, ctx) => {
      if (value === '') {
        ctx.addIssue({ code: 'custom', message: "Endpoint URL can't be blank" });
        return;
      }
      if (isCustomerIoDomain(value)) {
        ctx.addIssue({ code: 'custom', message: "Endpoint URL can't be a Customer.io domain" });
        return;
      }
      if (!isAbsoluteHttpUrl(value)) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid endpoint URL.' });
      }
    }),
  events: z.array(z.string()),
  resolution: z.boolean(),
  with_content: z.boolean(),
  enabled: z.boolean(),
});

export type ReportingWebhookFormInput = z.input<typeof reportingWebhookSchema>;
export type ReportingWebhookFormValues = z.output<typeof reportingWebhookSchema>;

/**
 * How a webhook is named back to the user after a save. A name is required, so the fallback only
 * covers a record that somehow reached the server without one.
 */
export function describeWebhook(name: string): string {
  return isEmptyString(name) ? 'Webhook' : name;
}
